import { useMediaQuery } from '@chakra-ui/react';
import { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { QueryTableContext, QueryTableContextValue } from './context/query-table-context';
import { MobileTable } from './mobile-table';
import { DraggableTableBase } from './draggable-table-base';
import { QueryTableDataResponse, QueryTableParams, TableBaseProps, TableFilter, TableInitialState, TablePagination, TableSortValue } from './types';
import { filterMapping } from './utils';
import { useFilter } from './hooks/use-filter';
import { useSort } from './hooks/use-sort';
import { usePagination } from './hooks/use-pagination';

type QueryTableProps<I extends EntityBase = EntityBase> = Pick<TableBaseProps<I>, 
  'columns' | 
  'onCreateClick' |
  'title' |   
  'keyProperty' |
  'selectedRow' |
  'onRowClick' |
  'createMenu' | 
  'additionalToolbar' |
  'secondaryToolbar' |
  'getRowBackgroundColor'
  > & TableInitialState<I> & {
    fetchData(params: QueryTableParams<I>): Promise<QueryTableDataResponse<I>>
    canDownload?: boolean
    ignoreQuery?: boolean
    onDragEnd?: (updatedArray: I[], oldIndex: number, newIndex: number) => void
} & PropsWithChildren;

export function QueryDraggableTable<I extends EntityBase = EntityBase>(props: QueryTableProps<I>) {
  const {
    fetchData,
    columns,
    children,
    canDownload,
    ignoreQuery,
    ...restProps
  } = props;
  const { pagination, setPagination } = usePagination({ initial: { page: 1, pageSize: 10 }, ignoreQuery });
  const { sort, setSort } = useSort<I>({ ignoreQuery, initial: props.initialSort });
  const { filter, setFilter } = useFilter<I>({ columns, ignoreQuery });
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<I[]>([]);
  const [total, setTotal] = useState(0);
  
  const fetch = useCallback(async (params: QueryTableParams<I>) => {
    setLoading(true);

    const { data, total } = await fetchData(params);

    setTotal(total);
    setData(data);
    setLoading(false);
  }, [fetchData]);

  const download = useCallback(async () => {
    setLoading(true);

    const { content, filename } = await fetchData({ 
      filter, 
      filterRaw: filterMapping<I>(filter, columns),
      sort: sort.name ? sort : { name: 'id', direction: 'asc' },
      pagination: { pageSize: 10, page: 1 },
      forDownload: true 
    });

    const linkElement = document.createElement('a');
    linkElement.href =
      'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
      content;
    linkElement.download = filename;
    document.body.appendChild(linkElement);
    linkElement.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
    document.body.removeChild(linkElement);
    setLoading(false);
  }, [columns, fetchData, filter, sort]);

  const onPaginationChange = useCallback((pagination: TablePagination) => {
    setPagination(pagination);
    fetch({
      pagination,
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, [setPagination, fetch, filter, columns, sort]);

  const onSortChange = useCallback((sort: TableSortValue<I>) => {
    setSort(sort);
    fetch({
      pagination,
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, [setSort, fetch, pagination, filter, columns]);

  const onFilterChange = useCallback((filter: TableFilter<I>[]) => {
    setPagination({ page: 1, pageSize: pagination.pageSize });
    setFilter(filter);
    fetch({
      pagination: { page: 1, pageSize: pagination.pageSize },
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, [setFilter, setPagination, pagination.pageSize, fetch, columns, sort]);

  useEffect(() => {
    fetch({
      pagination,
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, []);

  const contextValue: QueryTableContextValue<I> = useMemo(() => ({
    columns,
    data,
    filter,
    sort,
    setFilter: onFilterChange,
    setSort,
    pagination,
    setPagination,
    loading,
    refetch: () => fetch({ 
      pagination, 
      sort, 
      filterRaw: filterMapping<I>(filter, columns), 
      filter 
    }),
    setData,
  }), [columns, data, filter, sort, onFilterChange, setSort, loading, fetch, pagination, setPagination]);

  const [isDesktop] = useMediaQuery('(min-width: 768px)', {
    fallback: true
  });
  
  if (!isDesktop) {
    return <QueryTableContext.Provider value={contextValue as unknown as QueryTableContextValue<EntityBase>} >
      <MobileTable 
        columns={columns}
        data={data} 
        pagination={pagination}
        total={total}
        sort={sort}
        filter={filter || []}
        loading={loading}
        onFilterChange={onFilterChange}
        onPaginationChange={onPaginationChange}
        onSortChange={onSortChange}
        {...restProps}  
      />
    </QueryTableContext.Provider>;
  }

  return <QueryTableContext.Provider value={contextValue as unknown as QueryTableContextValue<EntityBase>} >
    <DraggableTableBase<I> 
      columns={columns}
      data={data} 
      pagination={pagination}
      total={total}
      sort={sort}
      filter={filter || []}
      loading={loading}
      onFilterChange={onFilterChange}
      onPaginationChange={onPaginationChange}
      onSortChange={onSortChange}
      onDownload={canDownload ? download : undefined}
      {...restProps}  
      onDragEnd={(updatedArray, oldIndex, newIndex) => {
        setData(updatedArray);
        restProps.onDragEnd?.(updatedArray, oldIndex, newIndex);
      }}
    />
    {children}
  </QueryTableContext.Provider>;
}

export function useQueryTable() {
  return useContext(QueryTableContext);
}

QueryDraggableTable.useQueryTable = useQueryTable;
